import { currentRefresh, currentToken } from './sessionToken';
import { fromBase64Url } from './base64url';





//////////////////////////////////////////////////
//////////////////////////////////////////////////
////   
////   USER / AUTH / TOKEN
////   
//////////////////////////////////////////////////
//////////////////////////////////////////////////

// Parsing is memoized on the raw token string: permission helpers may call
// getUserId() once per collection per render, and re-running the base64 decode + JSON.parse
// hundreds of times per paint is pure waste.
let tokenMemo = { raw: null, payload: null };

const getTokenObj = () => {

	// returns the decoded payload of the token as an object
	// NOTE: does NOT verify token is still valid - Not to be used for security, only for accessing the payload
	// The token is the one utils/session.js holds in memory (hydrated from the
	// platform's secure store at boot, KPR-1669), never read from storage here.

	const token = currentToken();

	if ( tokenMemo.raw === token ) return tokenMemo.payload;

	let payload = null;
	if ( token && ( token + '' ).split( '.' ).length > 1 ) {
		try {
			payload = JSON.parse( fromBase64Url( ( token + '' ).split( '.' )[ 1 ] ) ); // JWT segments are base64url (KPR-1480); standard base64 decodes the same
		}
		catch ( e ) {
			payload = null;
		}
	}
	tokenMemo = { raw: token, payload };
	return payload;

}

export function isTokenValid() {

	if ( getTokenExpiry( { relative: true } ) > 0 ) return true;
	// A device session (the phone app, KPR-1674) holds a refresh credential
	// beside its 1-hour token: its hour passing is not a sign-out, offline or
	// not. The api client renews it on the next 401, and only a refused
	// renewal ends the session. The web holds no credential, so a browser's
	// token is still over when its `exp` passes.
	return Boolean( currentRefresh() && getTokenObj()?.userId );

}

export function getTokenExpiry( { relative = false } = {} ) {

	// console.log( getTokenObj() );

	const exp = getTokenObj()?.exp;

	return ( relative ? secondsFromNow( exp ) : exp );

}

export function getTokenIssuedAt( { relative = false } = {} ) {

	return getTokenObj()?.iat;

}

export function getUserId() {

	return getTokenObj()?.userId + '';

}

export function getEmail() {

	return getTokenObj()?.email + '';

}

export function getFullName() {

	return getTokenObj()?.fullName + '';

}

export function getFirstName() {

	return getTokenObj()?.firstName + '';

}

export function getLastName() {

	return getTokenObj()?.lastName + '';

}

export function userRoles() {

	return getTokenObj()?.roles || [];

}

export function isAdmin() {

	// evaluated per call (not at module load) so it stays correct across
	// login/logout without a full page reload
	return userRoles().indexOf( 'admin' ) > -1;

}

export function hasRole( role ) {

	// same per-call evaluation as isAdmin() — never memoized at module load
	return !! role && userRoles().indexOf( role ) > -1;

}

export function isAttachAdmin() {

	// 'attach-admin' lifts the client-side attachment size precheck; plain
	// admins get it too. The server still has the final say on every upload.
	return hasRole( 'attach-admin' ) || isAdmin();

}


//////////////////////////////////////////////////
//////////////////////////////////////////////////
////   
////   STATICS
////   
//////////////////////////////////////////////////
//////////////////////////////////////////////////


// export const hostname = 'http://localhost:3000';
export const HOSTNAME = process.env.REACT_APP_API_SERVER; // 'http://10.0.1.181:3000'
const ABSOLUTE_HOST = true;
export const API_URL = ( ABSOLUTE_HOST ? HOSTNAME : '' ) + '/api';
// Liveness + release identity, at the API's origin root (not under /api).
export const API_HEALTH_URL = ( ABSOLUTE_HOST ? HOSTNAME : '' ) + '/health';
export const API_CARD_DEFS_URL = API_URL + '/card-definitions';
// Element sets (docs/plans/element-sets-and-me.md): the reusable element
// bundles a card attaches beside the hierarchy.
export const API_ELEMENT_SETS_URL = API_URL + '/element-sets';
export const API_ELEMENT_SET_URL = id => `${API_ELEMENT_SETS_URL}/${id}`;
export const API_ELEMENT_SET_PROMOTE_URL = id => `${API_ELEMENT_SET_URL( id )}/promote`;
export const API_CARD_ELEMENT_SET_MATERIALIZE_URL = ( cardId, setId ) => `${API_CARD_DEFS_URL}/${cardId}/element-sets/${setId}/materialize`;
export const API_CARD_ELEMENT_SET_EXTRACT_URL = cardId => `${API_CARD_DEFS_URL}/${cardId}/element-sets/extract`;
export const API_COLLECTION_CARDS_URL = (id) => `${API_URL}/collections/${id}/cards`;
export const API_ITEMS_URL = `${API_URL}/items`;
export const API_ITEMS_COUNT_URL = `${API_ITEMS_URL}/count`;
// The newest record of a card a person may read (KPR-1126): what a Home "Newest" pin opens.
export const API_ITEMS_NEWEST_URL = `${API_ITEMS_URL}/newest`;
// Per-card totals for one collection, in one request (the collection home).
export const API_ITEM_CARD_COUNTS_URL = `${API_ITEMS_URL}/counts`;
export const API_COLLECTIONS_URL = API_URL + '/collections';
export const API_PACKAGES_URL = API_URL + '/packages';
export const API_GLOBAL_COLLECTIONS_URL = API_URL + '/global-collections';
// Following one item (keepr-api docs/SCHEMA.md § Following an item): the
// item's own follow, and the person's list of what they follow.
export const API_ITEM_FOLLOW_URL = itemId => `${API_ITEMS_URL}/${itemId}/follow`;
export const API_ITEM_FOLLOWS_URL = API_URL + '/item-follows';
export const API_ADMIN_REVIEW_URL = API_URL + '/admin/review';
// Discover curation (keepr-api docs/SCHEMA.md § Discover is curated, KPR-387).
export const API_DISCOVER_CATEGORIES_URL = API_URL + '/discover/categories';
export const API_ADMIN_DISCOVER_URL = API_URL + '/admin/discover';
// Publishers (KPR-385; keepr-api docs/SCHEMA.md § Publishers): admin only.
export const API_ADMIN_PUBLISHERS_URL = API_URL + '/admin/publishers';
export const API_COLLECTION_CARDS_COUNT_URL = id => `${API_COLLECTIONS_URL}/${id}/cards/count`;
export const API_COLLECTION_COUNTS_URL = `${API_COLLECTIONS_URL}/counts`;
// The rebuilt tags (docs/plans/tags.md §3.8; keepr-api docs/SCHEMA.md § Tags):
// the vocabulary (?owner=me · ?collection_id=, &status=deleted), one tag, its
// restore; and the private applications store (per person, never on the item).
export const API_TAGS_URL = API_URL + '/tags';
export const API_TAG_URL = id => `${API_TAGS_URL}/${id}`;
export const API_TAG_RESTORE_URL = id => `${API_TAG_URL( id )}/restore`;
// An automatic tag's rule (T6a): GET / PUT the rule, POST a preview; and why
// an item carries its automatic tags.
export const API_TAG_RULE_URL = id => `${API_TAG_URL( id )}/rule`;
export const API_TAG_RULE_PREVIEW_URL = id => `${API_TAG_RULE_URL( id )}/preview`;
export const API_ITEM_TAG_REASONS_URL = itemId => `${API_ITEMS_URL}/${itemId}/tag-reasons`;
// "Me too" (KPR-390, keepr-api SCHEMA.md § Confirmations): an item's counts and
// the caller's own state (GET), one element's confirm / take back (POST /
// DELETE), and who confirmed — managers only (GET …/people).
export const API_ITEM_CONFIRMATIONS_URL = itemId => `${API_ITEMS_URL}/${itemId}/confirmations`;
export const API_ITEM_CONFIRMATION_URL = ( itemId, element ) => `${API_ITEM_CONFIRMATIONS_URL( itemId )}/${encodeURIComponent( element )}`;
export const API_ITEM_CONFIRMERS_URL = ( itemId, element ) => `${API_ITEM_CONFIRMATION_URL( itemId, element )}/people`;
// Who confirmed, as a CSV file (KPR-440): managers only, made on request.
export const API_ITEM_CONFIRMERS_EXPORT_URL = ( itemId, element ) => `${API_ITEM_CONFIRMERS_URL( itemId, element )}/export`;
// Comments on an item (KPE-005, KPR-306; keepr-api SCHEMA.md § Comments on
// items): the list (GET) and add (POST), one comment (GET / PATCH / DELETE)
// and its edit history (GET …/history). Session-only.
export const API_ITEM_COMMENTS_URL = itemId => `${API_ITEMS_URL}/${itemId}/comments`;
export const API_ITEM_COMMENT_URL = ( itemId, commentId ) => `${API_ITEM_COMMENTS_URL( itemId )}/${commentId}`;
export const API_ITEM_COMMENT_HISTORY_URL = ( itemId, commentId ) => `${API_ITEM_COMMENT_URL( itemId, commentId )}/history`;
export const API_TAG_APPLICATIONS_URL = API_URL + '/tag-applications';
// GET /api/tags/search?q= — the picker's and the palette's search (item tags by title).
export const API_TAGS_SEARCH_URL = API_TAGS_URL + '/search';
// The tag page's rows and counts (GET /api/tags/{id}/items, T3; the page is T5).
export const API_TAG_ITEMS_URL = id => `${API_TAG_URL( id )}/items`;
// T9: merge a tag into another (POST, `{ into, dryRun? }`), which of a
// template's tags the caller can use here (GET ?collection_id=&ids=), and the
// tag page exported (GET ?format=).
export const API_TAG_MERGE_URL = id => `${API_TAG_URL( id )}/merge`;
export const API_TAGS_USABLE_URL = API_TAGS_URL + '/usable';
export const API_TAG_EXPORT_URL = id => `${API_TAG_URL( id )}/export`;
// Combined lists (KPE-034; keepr-api docs/SCHEMA.md § Combined lists): one card
// across every collection a person lists. `resolve` and `query` read an unsaved
// definition (POSTed because it is a body); the rest are a person's saved lists.
export const API_COMBINED_LISTS_URL = API_URL + '/combined-lists';
export const API_COMBINED_LIST_URL = id => `${API_COMBINED_LISTS_URL}/${id}`;
export const API_COMBINED_LIST_ITEMS_URL = id => `${API_COMBINED_LIST_URL( id )}/items`;
export const API_COMBINED_LISTS_RESOLVE_URL = API_COMBINED_LISTS_URL + '/resolve';
export const API_COMBINED_LISTS_QUERY_URL = API_COMBINED_LISTS_URL + '/query';
export const API_COMBINED_LISTS_SUGGESTIONS_URL = API_COMBINED_LISTS_URL + '/suggestions';
export const API_USERS_URL = API_URL + '/users';
export const API_USER_URL = API_URL + '/user';
export const API_LOGIN_URL = API_URL + '/login';
// Device sessions (the phone app; keepr-api SCHEMA.md § Device sessions, KPR-1674).
export const API_AUTH_REFRESH_URL = API_URL + '/auth/refresh';
export const API_DEVICE_SESSIONS_URL = API_URL + '/device-sessions';
export const API_PROFILE_URL = API_URL + '/user-info';
export const API_CHANGE_PASSWORD = API_URL + '/change-password';
export const API_API_KEYS_URL = API_URL + '/api-keys';
// The OAuth authorization server behind the remote connector (services/oauth.js).
export const API_OAUTH_URL = API_URL + '/oauth';
export const API_USER_PREFERENCES_URL = API_URL + '/user-preferences';
export const API_TOUR_PROGRESS_URL = API_URL + '/tour-progress';
export const API_TOUR_PROGRESS_ONE_URL = tourId => `${API_TOUR_PROGRESS_URL}/${encodeURIComponent(tourId)}`;
export const API_FAVORITES_URL = API_URL + '/favorites';
export const API_HOME_URL = API_URL + '/home';
export const API_USERS_SEARCH_URL = API_URL + '/users/search';
export const API_CLONEABLE_GROUPS_URL = API_URL + '/groups/cloneable';
export const API_FILES_URL = API_URL + '/files';
export const API_ADMIN_EMAILS_URL = API_URL + '/admin/emails';
export const API_ADMIN_EMAIL_SUPPRESSIONS_URL = API_URL + '/admin/email-suppressions';
// Item attachments — files hung off one saved item, list + upload on the
// collection URL, download/delete on the member URL.
export const API_ITEM_ATTACHMENTS_URL = itemId => `${API_ITEMS_URL}/${itemId}/attachments`;
export const API_ITEM_ATTACHMENT_URL = ( itemId, attachmentId ) => `${API_ITEM_ATTACHMENTS_URL( itemId )}/${attachmentId}`;
export const API_ITEM_ATTACHMENT_DOWNLOAD_URL = ( itemId, attachmentId ) => `${API_ITEM_ATTACHMENT_URL( itemId, attachmentId )}/download`;
// The server-made sizes of a photo or a PDF's first page (`sm` 160 px square,
// `md` 800 px, `lg` 2,048 px; WebP). Readers of the item, never ledgered.
export const API_ITEM_ATTACHMENT_VARIANT_URL = ( itemId, attachmentId, size ) => `${API_ITEM_ATTACHMENT_URL( itemId, attachmentId )}/variants/${size}`;
// Held files (keepr-api PR 10a; the 10b contract): a form's uploads before
// the item is saved — private to their uploader, session only. Upload (with
// `card_id` and `element`), touch (an hour from now), delete (Cancel), and
// the held thumbnail.
export const API_HELD_FILES_URL = collectionId => `${API_COLLECTIONS_URL}/${collectionId}/held-files`;
export const API_HELD_FILE_URL = ( collectionId, fileId ) => `${API_HELD_FILES_URL( collectionId )}/${fileId}`;
export const API_HELD_FILE_TOUCH_URL = ( collectionId, fileId ) => `${API_HELD_FILE_URL( collectionId, fileId )}/touch`;
export const API_HELD_FILE_VARIANT_URL = ( collectionId, fileId, size ) => `${API_HELD_FILE_URL( collectionId, fileId )}/variants/${size}`;
// Turning a collection's attachments off says first how many items hold
// files in file elements (managers only): { boundItems, boundFiles, otherFiles }.
export const API_COLLECTION_ATTACHMENTS_SUMMARY_URL = collectionId => `${API_COLLECTIONS_URL}/${collectionId}/attachments/summary`;
// Admin-only cross-collection view of every attachment, plus one file's
// upload/download transaction log.
export const API_ADMIN_ATTACHMENTS_URL = API_URL + '/admin/attachments';
export const API_ADMIN_ATTACHMENT_TRANSACTIONS_URL = attachmentId => `${API_ADMIN_ATTACHMENTS_URL}/${attachmentId}/transactions`;
// Admin user administration (platform 'admin' role enforced by the API).
export const API_ADMIN_USERS_URL = API_URL + '/admin/users';
export const API_ADMIN_INVITES_URL = API_URL + '/admin/invites';
export const API_ADMIN_STYLE_GUIDE_EXPORT_URL = API_URL + '/admin/style-guide/export';

// The change log (docs/SCHEMA.md § History in keepr-api).
export const API_HISTORY_URL = API_URL + '/history';
export const API_CHANGE_URL = changeId => `${API_HISTORY_URL}/${changeId}`;
export const API_CHANGE_REVERT_URL = changeId => `${API_CHANGE_URL( changeId )}/revert`;
export const API_ITEM_HISTORY_URL = itemId => `${API_ITEMS_URL}/${itemId}/history`;
export const API_COLLECTION_HISTORY_URL = collectionId => `${API_COLLECTIONS_URL}/${collectionId}/history`;
export const API_CARD_HISTORY_URL = cardId => `${API_CARD_DEFS_URL}/${cardId}/history`;
export const API_USER_HISTORY_URL = userId => `${API_USERS_URL}/${userId}/history`;
export const API_ADMIN_HISTORY_URL = API_URL + '/admin/history';
// "Recently deleted" (delete audit F5): read-only lists whose rows restore through API_CHANGE_REVERT_URL.
export const API_MY_RECENTLY_DELETED_URL = API_URL + '/recently-deleted';
export const API_COLLECTION_RECENTLY_DELETED_URL = collectionId => `${API_COLLECTIONS_URL}/${collectionId}/recently-deleted`;
export const API_ADMIN_RECENTLY_DELETED_URL = API_URL + '/admin/recently-deleted';
// Settings → Imports (keepr-api utils/importUndo.js): batch-ingest calls
// grouped into imports, and undoing one. Session-only, manage.
export const API_COLLECTION_IMPORTS_URL = collectionId => `${API_COLLECTIONS_URL}/${collectionId}/imports`;
// Import a spreadsheet (KPR-1899): what a collection accepts, and batch ingest.
export const API_COLLECTION_SCHEMA_URL = collectionId => `${API_COLLECTIONS_URL}/${collectionId}/schema`;
export const API_COLLECTION_INGEST_URL = collectionId => `${API_COLLECTIONS_URL}/${collectionId}/ingest`;
export const API_IMPORT_UNDO_PREVIEW_URL = ( collectionId, importKey ) => `${API_COLLECTION_IMPORTS_URL( collectionId )}/${encodeURIComponent( importKey )}/revert-preview`;
export const API_IMPORT_UNDO_URL = ( collectionId, importKey ) => `${API_COLLECTION_IMPORTS_URL( collectionId )}/${encodeURIComponent( importKey )}/revert`;
// Change card (KPE-028, keepr-api SCHEMA.md § Change card): the dry run that
// works a mapping out over the selected items and hands back its counts.
export const API_CHANGE_CARD_DRY_RUN_URL = collectionId => `${API_COLLECTIONS_URL}/${collectionId}/change-card/dry-run`;
export const API_CHANGE_CARD_APPLY_URL = collectionId => `${API_COLLECTIONS_URL}/${collectionId}/change-card/apply`;
export const API_CHANGE_CARD_TASK_URL = (collectionId, taskId) => `${API_COLLECTIONS_URL}/${collectionId}/change-card/tasks/${taskId}`;
export const API_CHANGE_CARD_TASK_UNDO_URL = (collectionId, taskId) => `${API_COLLECTIONS_URL}/${collectionId}/change-card/tasks/${taskId}/undo`;
export const API_ADMIN_USER_URL = id => `${API_ADMIN_USERS_URL}/${id}`;
export const API_ADMIN_USER_AUTH_LOG_URL = id => `${API_ADMIN_USERS_URL}/${id}/auth-log`;
export const API_ADMIN_USER_FORCE_RESET_URL = id => `${API_ADMIN_USERS_URL}/${id}/force-reset`;
export const API_ADMIN_USERS_BULK_URL = `${API_ADMIN_USERS_URL}/bulk`;
export const API_ADMIN_USER_RESEND_VERIFICATION_URL = id => `${API_ADMIN_USERS_URL}/${id}/resend-verification`;
export const API_ADMIN_USER_DELETE_PREVIEW_URL = id => `${API_ADMIN_USERS_URL}/${id}/delete-preview`;
// The bulk dialog's totals: ?ids=a,b,c or the directory filter params.
export const API_ADMIN_USERS_DELETE_PREVIEW_URL = `${API_ADMIN_USERS_URL}/delete-preview`;
export const API_ADMIN_BLOCKLIST_URL = API_URL + '/admin/blocklist';
export const API_ADMIN_BLOCKLIST_ENTRY_URL = id => `${API_ADMIN_BLOCKLIST_URL}/${id}`;
export const API_ADMIN_EMAIL_ALLOWANCES_URL = API_URL + '/admin/email-allowances';
export const API_ADMIN_EMAIL_ALLOWANCE_URL = id => `${API_ADMIN_EMAIL_ALLOWANCES_URL}/${id}`;
export const API_ADMIN_ABUSE_SUMMARY_URL = API_URL + '/admin/abuse/summary';
export const API_ADMIN_TASKS_URL = API_URL + '/admin/tasks';
export const API_ADMIN_TASK_RUN_URL = name => `${API_ADMIN_TASKS_URL}/${encodeURIComponent(name)}/run`;
export const API_ADMIN_TASK_RUNS_URL = name => `${API_ADMIN_TASKS_URL}/${encodeURIComponent(name)}/runs`;
export const API_LOGOUT_ALL_URL = API_URL + '/logout-all';
export const API_USER_ME_URL = `${API_USER_URL}/me`;
export const API_USER_ME_DELETE_PREVIEW_URL = `${API_USER_ME_URL}/delete-preview`;
export const API_RESEND_VERIFICATION_URL = API_URL + '/resend-verification';
// Email delivery (keepr-api utils/emailEvents): the signed-in resume after a
// spam report, and the session-less preferences link in every mail's foot —
// the code is the path's last segment, never a query string.
export const API_EMAIL_DELIVERY_RESUME_URL = API_URL + '/email-delivery/resume';
export const API_EMAIL_PREFERENCES_URL = code => `${API_URL}/email-preferences/${encodeURIComponent(code)}`;
// One-round-trip KPI aggregate for the admin dashboard (?window=7d|30d|90d).
export const API_ADMIN_METRICS_URL = API_URL + '/admin/metrics';
// Infrastructure snapshots for the dashboard (admin only, read-only, no
// window): the MongoDB deployment and the S3 bucket census. Fetched once per
// page load, not on every period change — a bucket listing is not free.
export const API_ADMIN_MONGO_METRICS_URL = `${API_ADMIN_METRICS_URL}/mongo`;
export const API_ADMIN_S3_METRICS_URL = `${API_ADMIN_METRICS_URL}/s3`;
export const API_ADMIN_EMAIL_HEALTH_URL = `${API_ADMIN_METRICS_URL}/email`;
// How close people come to every tag limit (tags T11): the daily snapshots.
export const API_ADMIN_TAG_METRICS_URL = API_URL + '/admin/tag-metrics';
// How close people come to every charts limit (charts A3): the daily snapshots.
export const API_ADMIN_CHART_METRICS_URL = API_URL + '/admin/chart-metrics';
// What short-text patterns cost, per day and per pattern (?window=7d|30d|90d,
// like the KPI aggregate): the dashboard's Validation patterns section.
export const API_ADMIN_PATTERN_METRICS_URL = `${API_ADMIN_METRICS_URL}/patterns`;
// The data types' rules as daily aggregates — Unique values first (keepr-api utils/dataTypeMetrics.js).
export const API_ADMIN_DATA_TYPE_METRICS_URL = `${API_ADMIN_METRICS_URL}/data-types`;
// How close combined lists come to their limits (sources 25, saved lists 50; KPR-1020/1021).
export const API_ADMIN_COMBINED_LIST_METRICS_URL = `${API_ADMIN_METRICS_URL}/combined-lists`;
export const API_ADMIN_FOLLOW_METRICS_URL = `${API_ADMIN_METRICS_URL}/follows`;
// The admin half of the documentation portal (/docs): change log, line
// counts, atlases and the operator guides. See services/adminDocs.js.
export const API_ADMIN_DOCS_URL = API_URL + '/admin/docs';
// Platform-wide automation observability (admin only, read-only). The base is
// exported on its own because the two children are the whole surface — there
// is no GET on the base itself, and repairs happen on the collection-scoped
// automation routes above, never here.
export const API_ADMIN_AUTOMATIONS_URL = API_URL + '/admin/automations';
export const API_ADMIN_AUTOMATION_RUNS_URL = `${API_ADMIN_AUTOMATIONS_URL}/runs`;
export const API_ADMIN_AUTOMATION_METRICS_URL = `${API_ADMIN_AUTOMATIONS_URL}/metrics`;
// The other half of the same engine, observed the same way (admin only,
// read-only): every send and every silence across every collection, the four
// things that need a human, and the dashboard rollup. Same shape as the three
// above — no GET on the base, and repairs happen where the authority lives.
export const API_ADMIN_NOTIFICATIONS_URL = API_URL + '/admin/notifications';
// ⚠ `payload` IS PROJECTED OUT OF THIS unless `include=payload` asks for it by
// name, and that ask writes an `adminAudit` row BEFORE the rows are returned.
// See services/adminNotifications.js.
export const API_ADMIN_NOTIFICATION_DELIVERIES_URL = `${API_ADMIN_NOTIFICATIONS_URL}/deliveries`;
export const API_ADMIN_NOTIFICATION_HEALTH_URL = `${API_ADMIN_NOTIFICATIONS_URL}/health`;
export const API_ADMIN_NOTIFICATION_METRICS_URL = `${API_ADMIN_NOTIFICATIONS_URL}/metrics`;
export const API_VERIFY_EMAIL_URL = API_URL + '/verify-email';
export const API_FORGOT_PASSWORD_URL = API_URL + '/forgot-password';
export const API_RESET_PASSWORD_URL = API_URL + '/reset-password';
// Saved KQL filters for one collection (two tiers: 'user' and 'collection').
export const API_SAVED_FILTERS_URL = id => `${API_COLLECTIONS_URL}/${id}/saved-filters`;
// Saved charts for one collection (the same two tiers; none inherited, KPR-1084;
// charts A3), and the batch run that runs them (POST, up to 24 per request).
export const API_CHARTS_URL = id => `${API_COLLECTIONS_URL}/${id}/charts`;
export const API_CHARTS_RUN_URL = API_URL + '/charts/run';
export const API_DASHBOARDS_URL = id => `${API_COLLECTIONS_URL}/${id}/dashboards`;
// The dashboard a collection's Overview shows (charts A7): the cascade's read,
// and the manager's choice for everyone (its own door: server-managed).
export const API_OVERVIEW_URL = id => `${API_COLLECTIONS_URL}/${id}/overview`;
export const API_OVERVIEW_DASHBOARD_URL = id => `${API_COLLECTIONS_URL}/${id}/overview-dashboard`;
// Quick add templates for one collection (the same two tiers), plus resolve.
export const API_QUICK_ADD_TEMPLATES_URL = id => `${API_COLLECTIONS_URL}/${id}/quick-add-templates`;
// The buttons a person could press in a collection (KPR-382), and one press (KPR-548).
export const API_CARD_ACTIONS_URL = id => `${API_COLLECTIONS_URL}/${id}/card-actions`;
export const API_ITEM_ACTION_RUN_URL = ( itemId, actionId ) => `${API_ITEMS_URL}/${itemId}/actions/${actionId}/run`;
// One record's buttons, each with whether its condition holds there (KPR-1342).
export const API_ITEM_CARD_ACTIONS_URL = itemId => `${API_ITEMS_URL}/${itemId}/card-actions`;
// Multi add forms for one collection (the same two tiers), plus submit.
export const API_MULTI_ADD_FORMS_URL = id => `${API_COLLECTIONS_URL}/${id}/multi-add-forms`;
// Card layouts: how a card's items are arranged, cascading card -> collection
// -> user. Card-anchored, because a global card is arranged per collection.
export const API_CARD_LAYOUTS_URL = cardId => `${API_CARD_DEFS_URL}/${cardId}/layouts`;
export const API_CARD_TILE_LAYOUT_URL = cardId => `${API_CARD_DEFS_URL}/${cardId}/layouts/tile`;
export const API_CARD_TABLE_LAYOUT_URL = cardId => `${API_CARD_DEFS_URL}/${cardId}/layouts/table`;
export const API_COLLECTION_CARD_LAYOUTS_URL = id => `${API_COLLECTIONS_URL}/${id}/card-layouts`;
// Automation rules for one collection, plus one rule's run ledger. Every
// automation route nests under the collection because the API's manage-role
// pre-handler reads the collection id from the path.
export const API_AUTOMATIONS_URL = collectionId => `${API_COLLECTIONS_URL}/${collectionId}/automations`;
export const API_AUTOMATION_URL = ( collectionId, automationId ) => `${API_AUTOMATIONS_URL( collectionId )}/${automationId}`;
export const API_AUTOMATION_RUNS_URL = ( collectionId, automationId ) => `${API_AUTOMATION_URL( collectionId, automationId )}/runs`;
// The per-user notification inbox the notify action writes to. Owner-scoped by
// the token alone — there is no collection in the path, and no single-row GET
// (the list is the only read; /read and /read-all hang off this base).
export const API_NOTIFICATIONS_URL = API_URL + '/notifications';
// The notification CATALOG: every notification that can reach you at one tier,
// its settings already resolved and its provenance said out loud. Flat, not
// nested under a collection, because the global tier has no parent collection —
// the same reason /api/user-preferences is flat.
export const API_NOTIFICATIONS_CATALOG_URL = `${API_NOTIFICATIONS_URL}/catalog`;
// The settings overlay those resolved channels come out of: PUT writes one
// tier's row, DELETE drops it and lets the cascade fall through again.
export const API_NOTIFICATION_SETTINGS_URL = API_URL + '/notification-settings';
// The DEFINITIONS the editor writes: the raw tier rows, cardLayouts-style.
// Flat for the same reason the catalog is — the global tier has no parent
// collection — with the tier named in the query string instead of the path.
export const API_NOTIFICATION_DEFS_URL = API_URL + '/notification-defs';
export const API_NOTIFICATION_DEF_URL = id => `${API_NOTIFICATION_DEFS_URL}/${id}`;
// A collection-tier disable tombstone over a global key: the row stays and the
// cascade stops at it. Not a DELETE — a DELETE would let the global row
// through again.
export const API_NOTIFICATION_DEF_DISABLE_URL = id => `${API_NOTIFICATION_DEF_URL( id )}/disable`;
// Renders one definition against real data and sends nothing.
export const API_NOTIFICATION_DEF_PREVIEW_URL = id => `${API_NOTIFICATION_DEF_URL( id )}/preview`;
// "Send me a test": the one door that DOES deliver, to the caller and nobody
// else. Owed on the API side (docs/plans/notifications.md, PR 10).
export const API_NOTIFICATION_DEF_TEST_URL = id => `${API_NOTIFICATION_DEF_URL( id )}/test`;
// Per-user view history (recently viewed collections/cards/items). Owner-
// scoped by the token; POST records a view, /recent lists, DELETE clears.
export const API_VIEWS_URL = API_URL + '/views';
export const API_VIEWS_RECENT_URL = `${API_VIEWS_URL}/recent`;
// Item drafts: a person's unsaved new-item forms, private to them and
// session-only (keepr-api docs/SCHEMA.md § Item drafts).
export const API_DRAFTS_URL = API_URL + '/drafts';
// Compact /s/{code} links — the QR-able stand-in for a URL carrying a query.
export const API_SHORT_LINKS_URL = API_URL + '/short-links';
// Record codes (keepr-api docs/SCHEMA.md § Record codes): a short URL's code → its record.
export const API_RECORD_CODES_URL = API_URL + '/codes';
// Temporary public access links: the manage-gated administration half …
export const API_SHARE_LINKS_URL = id => `${API_COLLECTIONS_URL}/${id}/share-links`;
// … and the two PUBLIC endpoints they lead to. These never answer 401 (every
// failure is one flat 404), so they are safe to call with no token at all —
// the api client's 401 interceptor can never bounce a visitor off them.
export const API_PUBLIC_SHARE_URL = API_URL + '/share';
// The admins' review list of "Report this page" reports (PR 10c), and the
// revoke an admin applies to any share link from it.
export const API_ADMIN_SHARE_REPORTS_URL = API_URL + '/admin/share-reports';
export const API_ADMIN_REVOKE_SHARE_LINK_URL = linkId => `${API_URL}/admin/share-links/${linkId}/revoke`;
// KPR-520: a public form's collection slug, read and released by an admin.
export const API_ADMIN_PUBLIC_ADDRESS_URL = slug => `${API_URL}/admin/public-addresses/${encodeURIComponent(slug)}`;
export const API_ADMIN_RELEASE_PUBLIC_ADDRESS_URL = slug => `${API_URL}/admin/public-addresses/${encodeURIComponent(slug)}/release`;
// Server-rendered exports (?format=pdf|xlsx|json): one item, a filtered item
// list, a card definition, or a whole collection. Fetched as blobs — the
// filename is built client-side (see services/exports.js).
export const API_ITEM_EXPORT_URL = itemId => `${API_ITEMS_URL}/${itemId}/export`;
export const API_ITEMS_EXPORT_URL = `${API_ITEMS_URL}/export`;
export const API_CARD_EXPORT_URL = cardId => `${API_CARD_DEFS_URL}/${cardId}/export`;
export const API_COLLECTION_EXPORT_URL = collectionId => `${API_COLLECTIONS_URL}/${collectionId}/export`;
// STORED exports: an export as a noun — a named, scheduled definition with its
// own run ledger. Nested under the collection because the API's manage gate
// reads the collection id from the path, exactly like automations.
export const API_EXPORT_DEFS_URL = collectionId => `${API_COLLECTIONS_URL}/${collectionId}/exports`;
export const API_EXPORT_DEF_URL = ( collectionId, exportId ) => `${API_EXPORT_DEFS_URL( collectionId )}/${exportId}`;
export const API_EXPORT_RUNS_URL = ( collectionId, exportId ) => `${API_EXPORT_DEF_URL( collectionId, exportId )}/runs`;
export const API_EXPORT_RUN_FILE_URL = ( collectionId, exportId, runId ) => `${API_EXPORT_RUNS_URL( collectionId, exportId )}/${runId}/file`;
// Export everything (KPR-742): one zip of every item and file a person can read.
export const API_EXPORT_ALL_URL = collectionId => `${API_COLLECTIONS_URL}/${collectionId}/export-all`;
export const API_EXPORT_ALL_JOB_URL = ( collectionId, jobId ) => `${API_EXPORT_ALL_URL( collectionId )}/${jobId}`;
export const API_EXPORT_ALL_LINK_URL = ( collectionId, jobId ) => `${API_EXPORT_ALL_JOB_URL( collectionId, jobId )}/link`;

export const EMAIL_RE = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i;


//////////////////////////////////////////////////
//////////////////////////////////////////////////
////   
////   UTILITIES
////   
//////////////////////////////////////////////////
//////////////////////////////////////////////////


const secondsFromNow = sec => { 
	if ( sec === undefined || sec === null ) {
		return null;
	}
	// TODO - consolidate to date.js

	return parseInt( sec - parseInt( Date.now() / 1000, 10 ), 10 ); 
};



export function leadingZeros( value, numPlaces ) {
	while ( ( value + '' ).length < numPlaces ) {
		value = '0' + value;
	}

	return value;
}

export function deepEqual(object1, object2) {
	const keys1 = Object.keys(object1);
	const keys2 = Object.keys(object2);

	if (keys1.length !== keys2.length) {
		return false;
	}

	for (const key of keys1) {
		const val1 = object1[key];
		const val2 = object2[key];
		const areObjects = isObject(val1) && isObject(val2);
		if (
			( areObjects && !deepEqual(val1, val2) ) ||
			( !areObjects && val1 !== val2 )
		) {
			return false;
		}
	}

	return true;
}

function isObject(object) {
	return object != null && typeof object === 'object';
}
